'use client';

import '@maptiler/sdk/dist/maptiler-sdk.css';
import './map-marker.scss';

import {
  type GeoJSONSource,
  type Map,
  type Marker,
} from '@maptiler/sdk';
import * as maptilersdk from '@maptiler/sdk';
import { useAtomValue } from 'jotai';
import { type LngLatLike } from 'maplibre-gl';
import { type ReadonlyURLSearchParams } from 'next/navigation';
import {
  type ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getCookieCurrency } from '@/features/currency/cookies/cookie-currency';
import { listingMapHighlightAtom } from '@/features/listing/atoms/listing-map-highlight-atom';
import ListingsMapPreview from '@/features/listing/components/listings-map/listings-map-preview/listings-map-preview';
import mapStyle from '@/features/listing/components/listings-map/map/map-style';
import getMapListings from '@/features/listing/requests/get-map-listings';
import {
  FiltersNames,
  GeoBboxNames,
  PaginationNames,
} from '@/features/listing/types/listing-filters-types';
import {
  type ListingType,
  type PreviewListingsData,
} from '@/features/listing/types/listing-types';
import prepareListingsSearchParams from '@/features/listing/utils/prepare-listings-search-params';
import { type Place } from '@/features/place/types/place-types';
import {
  usePathname,
  useRouter,
} from '@/i18n/routing';
import Toggle from '@/ui/form-elements/toggle/toggle';

import styles from './map.module.scss';

interface MapRenderProps {
  coordinates: LngLatLike;
  listingType: ListingType;
  currentPlace: Place;
  searchParams: ReadonlyURLSearchParams;
}

// const maptiler = {
//   // accessToken: 'oxQ5YeTFJ13jkTv9eIN9pB67cKSSFhsV',
//   attribution: '&copy; <a href="https://www.maptiler.com/">MapTiler</a> &copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
//   url: 'https://map.roomster.com/data/v3/{z}/{x}/{y}.pbf?key=oxQ5YeTFJ13jkTv9eIN9pB67cKSSFhsV',
// };

const markerTemplate = (listingsIds: string[], listingsAmount: number) => {
  const html = `<div class="mapMarker" data-listings-ids="${listingsIds.join(' ')}">${String(listingsAmount)}</div>`;

  const element = document.createElement('div');
  element.innerHTML = html;
  return element;
};

export default function MapRender(props: MapRenderProps) {
  const {
    coordinates,
    currentPlace,
    listingType,
    searchParams,
  } = props;

  const router = useRouter();
  const pathname = usePathname();

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<Map | null>(null);
  const loadMapListingsFunction = useRef<(() => void) | null>(null);
  const previousScaleRadius = useRef<number | null>(null);
  const highlightedMarker = useRef<Element | null>(null);

  const listingMapHighlight = useAtomValue(listingMapHighlightAtom);

  const [isDragSearchEnabled,
    setIsDragSearchEnabled] = useState<boolean>(true);
  const [previewListingsData,
    setPreviewListingsData] = useState<PreviewListingsData | null>();

  const loadMapListings = () => {
    if (!map.current) return;

    function eventAction() {
      if (!map.current) return;

      const updatedParamsForLink = new URLSearchParams(searchParams);

      const mapBounds = map.current.getBounds();
      const southWest = mapBounds.getSouthWest();
      const northEast = mapBounds.getNorthEast();

      const LatitudeSouthWest = String(southWest.lat);
      const LatitudeNorthEast = String(northEast.lat);
      const LongitudeNorthEast = String(northEast.lng);
      const LongitudeSouthWest = String(southWest.lng);

      updatedParamsForLink.set(GeoBboxNames.LatitudeSouthWest, LatitudeSouthWest);
      updatedParamsForLink.set(GeoBboxNames.LatitudeNorthEast, LatitudeNorthEast);
      updatedParamsForLink.set(GeoBboxNames.LongitudeNorthEast, LongitudeNorthEast);
      updatedParamsForLink.set(GeoBboxNames.LongitudeSouthWest, LongitudeSouthWest);
      updatedParamsForLink.set('mapMove', 'true');

      map.current.off('moveend', eventAction);

      router.push(`${pathname}?${updatedParamsForLink.toString()}`);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }

    map.current.on('moveend', eventAction);
  };

  const handleDragSearchSwitch = (event: ChangeEvent<HTMLInputElement>) => {
    setIsDragSearchEnabled(event.target.checked);

    if (map.current) {
      if (event.target.checked) {
        map.current.on('dragend', loadMapListings);
        map.current.on('zoomend', loadMapListings);
        loadMapListingsFunction.current = loadMapListings;
      } else if (loadMapListingsFunction.current) {
        map.current.off('dragend', loadMapListingsFunction.current);
        map.current.off('zoomend', loadMapListingsFunction.current);
      }
    }
  };

  const handleClearListingsPreviewIds = () => {
    setPreviewListingsData(null);
  };

  // Map marker highlighting
  useEffect(() => {
    if (mapContainer.current) {
      if (listingMapHighlight) {
        if (highlightedMarker.current) {
          highlightedMarker.current.classList.remove('mapMarker_active');
        }

        const markerToHighlight = mapContainer.current.querySelector(`div[data-listings-ids~="${String(listingMapHighlight.id)}"]`);

        if (markerToHighlight) {
          markerToHighlight.classList.add('mapMarker_active');
          highlightedMarker.current = markerToHighlight;
        }
      }

      if (listingMapHighlight === null && highlightedMarker.current) {
        highlightedMarker.current.classList.remove('mapMarker_active');
        highlightedMarker.current = null;
      }
    }
  }, [listingMapHighlight]);

  useEffect(() => {
    if (map.current) {
      const currentCurrencyName = getCookieCurrency();
      const listingSearchParams = prepareListingsSearchParams(
        searchParams,
        listingType,
        currentPlace.bbox,
        true,
      );

      // reinit listeners to use fresh data
      if (loadMapListingsFunction.current) {
        map.current.off('dragend', loadMapListingsFunction.current);
        map.current.off('zoomend', loadMapListingsFunction.current);
      }

      // need to avoid map pins disappear, because we always get page number one
      listingSearchParams.delete(PaginationNames.PageNumber);

      getMapListings(listingSearchParams.toString(), currentCurrencyName)
        .then((data) => {
          if (!map.current) return;

          const latitudeSouthWest = Number(listingSearchParams.get(GeoBboxNames.LatitudeSouthWest));
          const latitudeNorthEast = Number(listingSearchParams.get(GeoBboxNames.LatitudeNorthEast));
          const longitudeNorthEast = Number(listingSearchParams.get(GeoBboxNames.LongitudeNorthEast));
          const longitudeSouthWest = Number(listingSearchParams.get(GeoBboxNames.LongitudeSouthWest));
          const listingsSource = map.current.getSource('listings') as (GeoJSONSource | undefined);

          if (listingsSource) {
            const scaleRadius = Number(listingSearchParams.get(FiltersNames.GeoRadiusScale));
            const camera = map.current.cameraForBounds([[longitudeSouthWest, latitudeSouthWest], [longitudeNorthEast, latitudeNorthEast]]);

            if (
              scaleRadius
              && scaleRadius !== 1
              && previousScaleRadius.current !== scaleRadius
            ) {
              const zoom = camera?.zoom ?? 0;
              const coefficient = 0.23;
              const delta = (1 - scaleRadius) * 10 * coefficient;

              map.current.setZoom(zoom + delta);
              previousScaleRadius.current = scaleRadius;
            }

            if (camera?.center) {
              map.current.setCenter(camera.center);
            }

            if (isDragSearchEnabled) {
              map.current.on('dragend', loadMapListings);
              map.current.on('zoomend', loadMapListings);
            }

            loadMapListingsFunction.current = loadMapListings;
            listingsSource.setData(data);
          }
        })
        .catch(() => null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlace, listingType, searchParams]);

  // Map init
  useEffect(() => {
    if (map.current) return; // prevent reinit map after first render

    const currentCurrencyName = getCookieCurrency();
    const listingSearchParams = prepareListingsSearchParams(
      searchParams,
      listingType,
      currentPlace.bbox,
      true,
    );

    // need to avoid map pins disappear, because we always get page number one
    listingSearchParams.delete(PaginationNames.PageNumber);

    getMapListings(listingSearchParams.toString(), currentCurrencyName)
      .then((data) => {
        const latitudeSouthWest = Number(listingSearchParams.get(GeoBboxNames.LatitudeSouthWest));
        const latitudeNorthEast = Number(listingSearchParams.get(GeoBboxNames.LatitudeNorthEast));
        const longitudeNorthEast = Number(listingSearchParams.get(GeoBboxNames.LongitudeNorthEast));
        const longitudeSouthWest = Number(listingSearchParams.get(GeoBboxNames.LongitudeSouthWest));
        const scaleRadius = listingSearchParams.get(FiltersNames.GeoRadiusScale);

        map.current = new maptilersdk.Map({
          bounds: [[longitudeSouthWest, latitudeSouthWest], [longitudeNorthEast, latitudeNorthEast]],
          center: coordinates,
          // @ts-expect-error - wrong types in lib
          container: mapContainer.current,
          crossSourceCollisions: true,
          doubleClickZoom: false,
          fullscreenControl: 'bottom-right',
          geolocateControl: false,
          scaleControl: true,
          // @ts-expect-error - wrong types in lib
          style: mapStyle,
          target: mapContainer.current,
        });

        map.current.on('load', () => {
          if (map.current) {
            map.current.addSource('planet', {
              tiles: ['https://map.roomster.com/data/v3/{z}/{x}/{y}.pbf?key=oxQ5YeTFJ13jkTv9eIN9pB67cKSSFhsV'],
              type: 'vector',
            });

            map.current.addSource('listings', {
              cluster: true,
              clusterRadius: 13,
              data,
              type: 'geojson',
            });

            // need to use it in marker creation
            map.current.addLayer({
              id: 'clusters',
              paint: {
                'circle-color': '#417054',
                'circle-radius': 15.5,
              },
              source: 'listings',
              type: 'circle',
            });

            if (scaleRadius && Number(scaleRadius) !== 1) {
              const camera = map.current.cameraForBounds([[longitudeSouthWest, latitudeSouthWest], [longitudeNorthEast, latitudeNorthEast]]);
              const zoom = camera?.zoom ?? 0;
              const coefficient = 0.23;
              const delta = (1 - Number(scaleRadius)) * 10 * coefficient;

              map.current.setZoom(zoom + delta);
            }

            // Change the cursor to a pointer when the mouse is over the places layer.
            map.current.on('mouseenter', 'clusters', () => {
              if (map.current) {
                map.current.getCanvas().style.cursor = 'pointer';
              }
            });

            // Change it back to a pointer when it leaves.
            map.current.on('mouseleave', 'clusters', () => {
              if (map.current) {
                map.current.getCanvas().style.cursor = '';
              }
            });

            map.current.on('click', 'clusters', (event) => {
              if (map.current) {
                const features = map.current.queryRenderedFeatures(event.point, { layers: ['clusters'] });
                const clusterId = features[0].id;
                const listingsSource = map.current.getSource('listings') as (GeoJSONSource | undefined);

                if (listingsSource) {
                  if (clusterId) {
                    setPreviewListingsData({
                      clusterId: Number(clusterId),
                      listingId: null,
                      listingsCount: features[0].properties.point_count as number,
                      listingsSource,
                    });
                  } else {
                    setPreviewListingsData({
                      clusterId: null,
                      listingId: features[0].properties.listingId as number,
                      listingsCount: 0,
                      listingsSource: null,
                    });
                  }
                }
              }
            });

            map.current.on('zoomend', loadMapListings);
            map.current.on('dragend', loadMapListings);

            loadMapListingsFunction.current = loadMapListings;
          }
        });

        const previousMarkers: Marker[] = [];

        // example was picked from here - https://docs.maptiler.com/sdk-js/examples/cluster-html/
        // there also describe mechanism of cache for markers,
        // but I don't think that we need this because we have diff logic from example
        const updateMarkers = () => {
          if (!map.current) return;

          const features = map.current.querySourceFeatures('listings');

          if (previousMarkers.length > 0) {
            previousMarkers.forEach((previousMarker) => previousMarker.remove());
          }

          // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
          // and add it to the map if it's not there already
          // eslint-disable-next-line no-restricted-syntax
          for (const feature of features) {
            if (feature.geometry.type === 'Point') {
              const coords = feature.geometry.coordinates;
              const featureProps = feature.properties;
              const listingsSource = map.current.getSource('listings') as (GeoJSONSource | undefined);

              // check if it is a cluster to request from it all listings ids
              if (featureProps.cluster && listingsSource) {
                // collect ids to set them as data attribute to marker
                // to have easy way to select cluster by listing id
                listingsSource.getClusterLeaves(Number(feature.id), 500, 0)
                  .then((mapListingsFeaturesData) => {
                    if (!map.current) return;

                    const listingsIds: string[] = [];

                    mapListingsFeaturesData.forEach((listingFeature) => {
                      if (listingFeature.properties) {
                        listingsIds.push(listingFeature.properties.listingId as string);
                      }
                    });

                    const marker = new maptilersdk.Marker({ element: markerTemplate(listingsIds, featureProps.point_count as number) }).setLngLat(coords as [number, number]);

                    previousMarkers.push(marker);
                    marker.addTo(map.current);
                  })
                  .catch(() => null);
              } else {
                const marker = new maptilersdk.Marker({ element: markerTemplate([String(featureProps.listingId)], 1) }).setLngLat(coords as [number, number]);

                previousMarkers.push(marker);
                marker.addTo(map.current);
              }
            }
          }
        };

        map.current.on('data', (event) => {
          // @ts-expect-error - example was picked for here https://docs.maptiler.com/sdk-js/examples/cluster-html/
          // mb maptiler has wrong types for this
          if (!map.current || event.sourceId !== 'listings' || !event.isSourceLoaded) return;

          // map.current.on('move', updateMarkers);
          map.current.on('dragend', updateMarkers);
          map.current.on('zoomend', updateMarkers);
          updateMarkers();
        });
      })
      .catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates, currentPlace, listingType, searchParams]);

  return (
    <div className={styles.map}>
      <div className={styles.map_dragControl}>
        <Toggle
          id="drag-map"
          type="checkbox"
          checked={isDragSearchEnabled}
          onChange={handleDragSearchSwitch}
        >
          <span className={styles.map_dragText}>Search as I move the map</span>
        </Toggle>
      </div>

      <div ref={mapContainer} className={styles.map_container}>
        {previewListingsData && (
          <ListingsMapPreview
            previewListingsData={previewListingsData}
            onClose={handleClearListingsPreviewIds}
          />
        )}
      </div>
    </div>
  );
}
